import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

const CONTROL_CHARACTERS = /\p{Cc}+/gu;
const MULTI_WHITESPACE = /\s+/g;
const REPLACEMENT_CHAR = /\uFFFD/g;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const safeDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());

const resolveDate = (input?: string | number | Date | null): Date | null => {
  if (input === null || input === undefined || input === '') {
    return null;
  }

  if (input instanceof Date) {
    return safeDate(input) ? input : null;
  }

  if (typeof input === 'number') {
    const parsed = new Date(input);
    return safeDate(parsed) ? parsed : null;
  }

  if (typeof input === 'string') {
    if (!input.trim()) {
      return null;
    }
    const parsed = new Date(input);
    return safeDate(parsed) ? parsed : null;
  }

  return null;
};

const DEFAULT_LOCALE = 'en-US';

export function formatDate(
  input?: string | number | Date | null,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
  fallback = '-'
): string {
  const date = resolveDate(input);
  if (!date) {
    return fallback;
  }

  return new Intl.DateTimeFormat(DEFAULT_LOCALE, options).format(date);
}

export function formatTimestamp(
  input?: string | number | Date | null,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
  fallback = '-'
): string {
  const date = resolveDate(input);
  if (!date) {
    return fallback;
  }

  return new Intl.DateTimeFormat(DEFAULT_LOCALE, options).format(date);
}

export const sanitizeDisplayText = (input?: string | null): string => {
  if (input === null || input === undefined) {
    return '';
  }

  let working = String(input);
  if (!working) {
    return '';
  }

  working = working.replace(REPLACEMENT_CHAR, '');
  working = working.replace(CONTROL_CHARACTERS, ' ');

  try {
    working = working.normalize('NFKC');
  } catch {
    // Some environments may not support String.prototype.normalize; ignore failures.
  }

  working = working.replace(MULTI_WHITESPACE, ' ').trim();

  return working;
};
